import { useEffect } from 'react';

/**
 * How much of the bottom of the layout viewport the on-screen keyboard covers, and how far the
 * browser has scrolled the visible area down within it.
 *
 * Mobile browsers open the keyboard over the page rather than shrinking it (iOS Safari always,
 * Chrome on Android by default), so `position: fixed; bottom: 0` — every modal in this app is a
 * bottom sheet — stays behind the keyboard. The visual viewport is the part still visible.
 */
export const keyboardInset = ({
  layoutHeight,
  visibleHeight,
  visibleTop
}: {
  layoutHeight: number;
  visibleHeight: number;
  visibleTop: number;
}) => ({
  bottom: Math.max(0, Math.round(layoutHeight - visibleHeight - visibleTop)),
  top: Math.max(0, Math.round(visibleTop))
});

/**
 * Keeps `--keyboard-inset` and `--visible-top` on <html> in step with the on-screen keyboard, so
 * a bottom sheet can sit just above it (App.scss) instead of the operator having to scroll down to
 * reach the field they are typing in. Both stay 0px wherever there is no keyboard, or no visual
 * viewport to ask (an old WebView), which leaves the layout exactly as it was.
 */
export function useKeyboardInset() {
  useEffect(() => {
    const viewport = window.visualViewport;
    if (!viewport) return undefined;
    const root = document.documentElement;
    let frame = 0;
    const apply = () => {
      const { bottom, top } = keyboardInset({
        // Not innerHeight: that counts a horizontal scrollbar, which the visual viewport does not,
        // and would lift every sheet ~15px on a desktop page that scrolls sideways.
        layoutHeight: root.clientHeight,
        visibleHeight: viewport.height,
        visibleTop: viewport.offsetTop
      });
      root.style.setProperty('--keyboard-inset', `${bottom}px`);
      root.style.setProperty('--visible-top', `${top}px`);
    };
    // The keyboard animates in; once per frame is plenty.
    const update = () => {
      cancelAnimationFrame(frame);
      frame = requestAnimationFrame(apply);
    };
    apply();
    viewport.addEventListener('resize', update);
    viewport.addEventListener('scroll', update);
    return () => {
      cancelAnimationFrame(frame);
      viewport.removeEventListener('resize', update);
      viewport.removeEventListener('scroll', update);
      root.style.removeProperty('--keyboard-inset');
      root.style.removeProperty('--visible-top');
    };
  }, []);
}

export default useKeyboardInset;
